// UI strings for syllables and stress beside the words (docs/next/fb-syllables-everywhere.md,
// fb-stress-why-in-popup.md): the setting "Tavut tekstissä" (syllables/syllables.settings.tsx) and the
// word sheet's one-line answer to "why is the stress here" (components/StressWhy.tsx), opened into
// the syllables' weights and the rule's step. The step sentences, weights and reasons are the stress
// drill's own (i18n stressDrill.why / weight / reason), so both say it the same way.
// `{r}` is why the syllable is heavy (stressDrill.reason). Style: docs/FINNISH-STYLE.md.
import { defineArea } from '../area';

export const syllablesFi = {
  setting: {
    title: 'Tavut',
    label: 'Tavut oppitunneilla ja kääntäjässä',
    off: 'Piilossa',
    on: 'Näkyvissä',
    /** The example under the control: the word as it then shows. */
    example: 'Näkyvissä: tavut pisteillä erotettuina ja painollinen tavu lihavoituna, esimerkiksi',
  },
  why: {
    /** One line per rule (engine rules/stress-weight.ts). */
    line: {
      'two-syllables': 'Paino ensimmäiselle: sanassa on kaksi tavua',
      'penult-heavy': 'Paino toiseksi viimeiselle: tavu on raskas ({r})',
      'antepenult-heavy': 'Paino kolmanneksi viimeiselle: toiseksi viimeinen on kevyt, tämä raskas ({r})',
      'both-light': 'Paino jää toiseksi viimeiselle: se ja sitä edeltävä tavu ovat kevyitä',
      command: 'Paino viimeiselle: käskymuoto',
      lengthening: 'Paino viimeiselle: luettelon viimeinen sana (ja-pidennys)',
    },
    /** A form that may also be read as a command or a lengthened last word of a list. */
    maybe: {
      command: 'Jos sana on käsky, paino on viimeisellä tavulla.',
      lengthening: 'Jos sana on luettelon viimeinen (ja-pidennys), paino on viimeisellä tavulla.',
    },
    oneSyllable: 'Paino ainoalle tavulle: sanassa on yksi tavu',
    /** Peterson's own stress, not the rule's (a borrowing, daor). */
    own: 'Peterson painottaa tätä sanaa toisin kuin sääntö neuvoo',
    /** No explanation the rule can give for sure. */
    unsure: 'Tämän sanan painolle ei ole varmaa selitystä',
    /** The syllable list (screen readers). */
    syllables: 'Tavut: raskaat ja kevyet',
    /** Beside the stressed syllable in the list. */
    stressed: 'painollinen',
  },
  /** Under the stress line on a dictionary entry and a lesson's word (components/StressWord.tsx `legend`). */
  weightLegend: 'Paksu viiva: raskas tavu · ohut: kevyt · isot kirjaimet: painollinen tavu',
};

export const syllablesEn: typeof syllablesFi = {
  setting: {
    title: 'Syllables',
    label: 'Syllables in lessons and the translator',
    off: 'Hidden',
    on: 'Shown',
    example: 'Shown: syllables split with dots and the stressed one in bold, for example',
  },
  why: {
    line: {
      'two-syllables': 'Stress on the first syllable: the word has two',
      'penult-heavy': 'Stress on the penult: the syllable is heavy ({r})',
      'antepenult-heavy': 'Stress on the antepenult: the penult is light, this one heavy ({r})',
      'both-light': 'Stress stays on the penult: it and the syllable before it are light',
      command: 'Stress on the last syllable: a command',
      lengthening: 'Stress on the last syllable: the last word of a list (“and” lengthening)',
    },
    maybe: {
      command: 'If the word is a command, the stress is on the last syllable.',
      lengthening: 'If the word is the last of a list (“and” lengthening), the stress is on the last syllable.',
    },
    oneSyllable: 'Stress on the only syllable: the word has one',
    own: 'Peterson stresses this word differently from the rule',
    unsure: 'There is no sure explanation for this word’s stress',
    syllables: 'Syllables: heavy and light',
    stressed: 'stressed',
  },
  weightLegend: 'Thick line: heavy syllable · thin: light · capitals: stressed syllable',
};

// Messages.syllables (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('syllables', syllablesFi, syllablesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof syllablesFi> {}
}
